import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { MdLogin, MdOutlineMail, MdVisibility, MdVisibilityOff } from 'react-icons/md'
import { HiOutlineLockClosed } from 'react-icons/hi'
import {
  errorToast,
  signInAuthWithEmailAndPassword,
  successToast,
} from '../../utils/firebase/firebase.utils'
import useAuth from '../../hooks/useAuth'
import './login.styles.scss'

const defaultFormFields = {
  email: '',
  password: '',
}

const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from?.pathname || '/manage'
  const { currentUser, authReady } = useAuth()

  const [formFields, setFormFields] = useState(defaultFormFields)
  const [submitting, setSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { email, password } = formFields

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormFields((prev) => ({ ...prev, [name]: value }))
  }

  const handleLogin = async (event) => {
    event.preventDefault()
    setSubmitting(true)

    try {
      await signInAuthWithEmailAndPassword(email.trim(), password)
      setFormFields(defaultFormFields)
      successToast('Logging you in..!')
    } catch (error) {
      switch (error.code) {
        case 'auth/wrong-password':
          errorToast('Oops..! Wrong Password')
          break
        case 'auth/user-not-found':
          errorToast('Oops..! User not found. Check email')
          break
        case 'auth/invalid-login-credentials':
        case 'auth/invalid-credential':
          errorToast('Oops..! Invalid login credentials')
          break
        default:
          errorToast('User Login Error')
          console.log('User Login Error: ', error)
          break
      }
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (authReady && currentUser) {
      navigate(from, { replace: true })
    }
  }, [currentUser, authReady, from, navigate])

  return (
    <section className="login-v2">
      <div className="login-v2__wash" aria-hidden="true" />
      <div className="login-v2__glow login-v2__glow--a" aria-hidden="true" />
      <div className="login-v2__glow login-v2__glow--b" aria-hidden="true" />
      <div className="login-v2__glow login-v2__glow--c" aria-hidden="true" />
      <div className="login-v2__mark" aria-hidden="true" />

      <div className="login-v2__inner">
        <div className="login-v2__intro">
          <p className="login-v2__eyebrow">Kitchen access</p>
          <h1>
            <span className="login-v2__script">Staff</span>
            <span className="login-v2__solid">sign in</span>
          </h1>
          <p className="login-v2__lead">
            Admin access only. Public registration is closed.
          </p>

          <ul className="login-v2__perks">
            <li>Manage inquiries</li>
            <li>Review pre-orders</li>
            <li>Update page content</li>
          </ul>
        </div>

        <div className="login-v2__panel">
          <div className="login-v2__panel-mark" aria-hidden="true" />
          <div className="login-v2__stamp" aria-hidden="true">
            Staff
            <span>only</span>
          </div>

          <div className="login-v2__panel-body">
            <form className="login-form" onSubmit={handleLogin} noValidate>
              <div className="login-form__head">
                <p className="login-form__eyebrow">Welcome back</p>
                <h2>Sign in to manage</h2>
                <p>Use your staff email and password.</p>
              </div>

              <label className="login-form__field">
                <span>Email</span>
                <div className="login-form__control">
                  <MdOutlineMail size={18} aria-hidden="true" />
                  <input
                    name="email"
                    type="email"
                    value={email}
                    onChange={handleChange}
                    autoComplete="username"
                    required
                    placeholder="you@cakebysandy.com"
                  />
                </div>
              </label>

              <label className="login-form__field">
                <span>Password</span>
                <div className="login-form__control">
                  <HiOutlineLockClosed size={18} aria-hidden="true" />
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="login-form__reveal"
                    onClick={() => setShowPassword((open) => !open)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
                  </button>
                </div>
              </label>

              <button type="submit" className="login-form__submit" disabled={submitting}>
                <span>{submitting ? 'Signing in…' : 'Sign in'}</span>
                <MdLogin size={18} aria-hidden="true" />
              </button>

              <div className="login-form__foot">
                <p>Need access? Ask an admin to create your staff account in the CMS.</p>
                <Link to="/">Back to site</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default LoginPage
